import { useState } from "react";
import type { KeyboardEvent } from "react";
import { Table } from "antd";
import { InsightExecution } from "@/hooks/useInsightResultsQuery";
import {
  formatISODateToYYYYMMDD,
  formatLongDate,
  getTime,
} from "@/utils/dateUtils";
import ExplanationDropdown from "./ExplanationDropdown";
import InsightAnswer from "./InsightAnswer";
import InsightRunExplanation from "./InsightRunExplanation";
import { toAnswerExcerpt } from "./insights.utils";

type InsightExecutionsTableProps = {
  executions: InsightExecution[];
  isLoading?: boolean;
  triggeredDates?: Set<string>;
};

const PAGE_SIZE = 10;

const RunTimestamp = ({ createdAt }: { createdAt: string }) => (
  <div className="flex flex-col">
    <span>{formatLongDate(createdAt, "en-US")}</span>
    <span className="text-[#00000073]">{getTime(createdAt)}</span>
  </div>
);

const ExecutionDetails = ({ execution }: { execution: InsightExecution }) => (
  <div className="flex flex-col gap-3 px-2 py-1">
    <InsightAnswer answer={execution.av_answer} />
    <ExplanationDropdown label="EXPLANATION">
      <InsightRunExplanation run={execution} />
    </ExplanationDropdown>
  </div>
);

/**
 * Every run of an insight, most recent first — the latest row is the current
 * evaluation. A row previews its answer; clicking it opens the full answer and
 * its explanation below, so a long table or wall of text never lands in a
 * cell. Rows are highlighted when that run's date matches a triggered alert.
 */
export default function InsightExecutionsTable({
  executions,
  isLoading = false,
  triggeredDates = new Set(),
}: InsightExecutionsTableProps) {
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const toggleDetails = (id: string) =>
    setExpandedIds((ids) =>
      ids.includes(id) ? ids.filter((openId) => openId !== id) : [...ids, id],
    );

  const toggleOnEnterOrSpace = (
    event: KeyboardEvent<HTMLElement>,
    id: string,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    toggleDetails(id);
  };

  const columns = [
    {
      title: "Run",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (createdAt: string) => <RunTimestamp createdAt={createdAt} />,
    },
    {
      title: "Answer",
      dataIndex: "av_answer",
      key: "av_answer",
      render: (answer: string | null) => (
        <span className="text-sm text-[#2B4752]">
          {toAnswerExcerpt(answer)}
        </span>
      ),
    },
  ];

  return (
    <div data-testid="insight-executions-table">
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={executions}
        pagination={{ pageSize: PAGE_SIZE, hideOnSinglePage: true }}
        locale={{ emptyText: "No past executions yet." }}
        rowClassName={(execution) =>
          triggeredDates.has(formatISODateToYYYYMMDD(execution.created_at))
            ? "cursor-pointer bg-[#EBD1E0]"
            : "cursor-pointer"
        }
        onRow={(execution) => ({
          tabIndex: 0,
          "aria-expanded": expandedIds.includes(execution.id),
          onClick: () => toggleDetails(execution.id),
          onKeyDown: (event) => toggleOnEnterOrSpace(event, execution.id),
        })}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: expandedIds,
          expandedRowRender: (execution: InsightExecution) => (
            <ExecutionDetails execution={execution} />
          ),
        }}
      />
    </div>
  );
}
